import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import {
  detectPlatform,
  signInWithAppleJS,
  signInWithOAuthProtocol,
} from '@/lib/apple-auth';
import { Loader2 } from 'lucide-react';

interface AppleSignInButtonProps {
  className?: string;
  theme?: 'black' | 'white' | 'white-outline';
  type?: 'sign-in' | 'continue' | 'sign-up';
}

const APPLE_TYPE_MAP = {
  'sign-in': 'sign in',
  'continue': 'continue',
  'sign-up': 'sign up',
} as const;

export function AppleSignInButton({
  className = '',
  theme = 'black',
  type = 'sign-in',
}: AppleSignInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [sdkFailed, setSdkFailed] = useState(false);

  // Render Apple's official button
  useEffect(() => {
    const el = containerRef.current;
    if (!el || isLoading) return;

    // Clear previous render
    const btnDiv = el.querySelector('#appleid-signin');
    if (btnDiv) btnDiv.innerHTML = '';

    // Wait for Apple JS SDK
    const render = () => {
      if ((window as any).AppleID?.auth) {
        try {
          (window as any).AppleID.auth.renderButton?.();
        } catch {
          // Fallback: SDK auto-renders based on data attributes
        }
        // Check if SDK actually rendered something visible
        setTimeout(() => {
          const btn = el.querySelector('#appleid-signin');
          if (btn && btn.clientHeight < 10) {
            console.warn('[AppleSignIn] SDK button did not render, using fallback');
            setSdkFailed(true);
          }
        }, 300);
      } else {
        console.warn('[AppleSignIn] Apple JS SDK not available, using fallback');
        setSdkFailed(true);
      }
    };

    const timer = setTimeout(render, 100);
    return () => clearTimeout(timer);
  }, [theme, type, isLoading]);

  const handleAppleSignIn = async () => {
    setIsLoading(true);

    try {
      const platform = detectPlatform();

      if (platform === 'android') {
        signInWithOAuthProtocol('buxbox');
        return;
      }

      const { idToken, code, user } = await signInWithAppleJS();

      navigate('/auth-loading', {
        state: { idToken, code, user, platform },
        replace: true,
      });
    } catch (error: any) {
      console.error('[AppleSignIn] Error:', error);
      const message = error?.message || 'Could not sign in with Apple. Please try again.';
      if (message !== 'Sign in cancelled') {
        toast({
          title: 'Sign in failed',
          description: message,
          variant: 'destructive',
        });
      }
      setIsLoading(false);
    }
  };

  const logoColor = theme === 'black' ? '#FFFFFF' : '#000000';

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {isLoading ? (
        /* Loading state overlay - matches Apple button dimensions */
        <button
          disabled
          className="inline-flex items-center justify-center gap-2 w-full disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            backgroundColor: theme === 'black' ? '#000000' : '#FFFFFF',
            color: theme === 'black' ? '#FFFFFF' : '#000000',
            border: theme === 'white-outline' ? '1px solid #000000' : 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize: '17px',
            fontWeight: 500,
            letterSpacing: '-0.22px',
            lineHeight: '1.18',
            height: '48px',
            borderRadius: '14px',
            padding: '0 16px',
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label="Signing in with Apple"
        >
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: logoColor }} />
          <span>Signing in…</span>
        </button>
      ) : (
        /* Apple's official button OR custom fallback */
        <div
          className="relative w-full"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAppleSignIn();
          }}
          style={{ cursor: 'pointer' }}
        >
          {sdkFailed ? (
            /* Custom fallback button matching Apple HIG style */
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 w-full"
              style={{
                backgroundColor: theme === 'black' ? '#000000' : '#FFFFFF',
                color: theme === 'black' ? '#FFFFFF' : '#000000',
                border: theme === 'white-outline' ? '1px solid #000000' : theme === 'white' ? '1px solid #d1d1d6' : 'none',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: '17px',
                fontWeight: 500,
                letterSpacing: '-0.22px',
                lineHeight: '1.18',
                height: '48px',
                borderRadius: '14px',
                padding: '0 16px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.714 9.384c-.024-2.426 1.98-3.59 2.07-3.648-1.126-1.648-2.88-1.874-3.504-1.9-1.492-.152-2.914.88-3.672.88-.758 0-1.93-.858-3.172-.834-1.632.024-3.136.95-3.976 2.414-1.696 2.944-.434 7.308 1.22 9.698.808 1.168 1.772 2.482 3.038 2.434 1.22-.048 1.68-.79 3.154-.79 1.474 0 1.886.79 3.172.766 1.312-.024 2.146-1.192 2.948-2.364.93-1.356 1.312-2.668 1.336-2.736-.03-.012-2.564-.984-2.59-3.904l-.024.004zM11.284 2.52C11.95 1.71 12.406.598 12.286-.5c-.932.038-2.062.62-2.73 1.402-.6.694-1.124 1.802-.984 2.866 1.04.08 2.102-.528 2.712-1.248z" fill={logoColor}/>
              </svg>
              <span>Continue with Apple</span>
            </button>
          ) : (
            <div
              id="appleid-signin"
              data-mode="center-align"
              data-type="continue"
              data-color={theme === 'white-outline' ? 'white' : theme}
              data-border="false"
              data-border-radius="14"
              data-width="100%"
              data-height="48"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </div>
      )}
    </div>
  );
}
